import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { getInitials } from 'support/helpers/generic/generic';
import { Image } from 'components/Image/Image';
import { ApplicationLogoSmall } from 'components/Logo/ApplicationLogoSmall/ApplicationLogoSmall';

const sizeClasses = {
  wrapper: {
    small: 'w-6 h-6',
    base: 'w-10 h-10',
    large: 'w-12 h-12',
  },
  image: {
    small: 'w-3 h-3',
    base: 'w-7 h-7',
    large: 'w-9 h-9',
  },
};

type SIZE_VARIANTS = 'small' | 'base' | 'large';

export const PartnerLogo = ({
  imageUrl,
  name,
  size = 'base',
  fullSize = false,
  isLoading = false,
  knownPartner = true,
  className,
  imageClassName,
}: {
  imageUrl: string | undefined | null;
  name?: string;
  isLoading?: boolean;
  size?: SIZE_VARIANTS;
  fullSize?: boolean;
  knownPartner?: boolean;
  className?: string;
  imageClassName?: string;
}) => {
  const wrapperClasses = fullSize
    ? 'shrink-0 flex items-center justify-center overflow-hidden'
    : 'shrink-0 border border-gray-200 bg-white rounded-sm flex items-center justify-center overflow-hidden';
  const imageClasses = fullSize ? 'h-full w-full' : 'object-contain';

  if (isLoading) {
    return (
      <PartnerLogoSkeleton className={cn(fullSize ? sizeClasses.wrapper[size] : sizeClasses.image[size], className)} />
    );
  }

  return (
    <div className={cn(sizeClasses.wrapper[size], wrapperClasses, className)}>
      {knownPartner ? (
        imageUrl ? (
          <Image
            // fullSize's h-full w-full must outrank any caller sizing in imageClassName.
            className={cn(sizeClasses.image[size], imageClassName, imageClasses)}
            src={imageUrl}
            alt=""
            fallback={
              name ? <PartnerLogoInitials name={name} /> : <ApplicationLogoSmall className={sizeClasses.image[size]} />
            }
          />
        ) : name ? (
          <PartnerLogoInitials name={name} />
        ) : (
          <ApplicationLogoSmall className={sizeClasses.image[size]} />
        )
      ) : (
        <UnkownPartnerLogo className={sizeClasses.image[size]} />
      )}
    </div>
  );
};

const PartnerLogoInitials = ({ name }: { name: string }) => {
  return <span className="text-xs font-medium text-gray-700">{getInitials(name, 2)}</span>;
};

const PartnerLogoSkeleton = ({ className }: { className: string }) => {
  return <div className={cn('shrink-0 animate-pulse rounded-sm bg-gray-200 leading-relaxed', className)} />;
};

const UnkownPartnerLogo = ({ className }: { className: string }) => {
  return (
    <div className="flex size-full shrink-0 items-center justify-center rounded-sm bg-gray-50 text-gray-400">
      <QuestionMarkCircleIcon className={className} />
    </div>
  );
};
